import { Component } from '@theme/component';

/**
 * @typedef {object} PouchOffer
 * @property {number} id
 * @property {boolean} available
 * @property {number} price
 * @property {boolean} requiresSellingPlan
 * @property {number} sellingPlanId
 * @property {string} sellingPlanName
 * @property {number} subscriptionPrice
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLScriptElement} config
 * @property {HTMLElement} originalPrice
 * @property {HTMLElement} pouchPrice
 * @property {HTMLElement} saving
 *
 * @extends {Component<Refs>}
 */
class PowderPouchUpsell extends Component {
  requiredRefs = ['dialog', 'config', 'originalPrice', 'pouchPrice', 'saving'];
  /** @type {PouchOffer | null} */
  offer = null;
  /** @type {AbortController | undefined} */
  #listeners;
  /** @type {((choice: 'pouch' | 'jars' | 'cancel') => void) | undefined} */
  #resolve;
  #ownsScrollLock = false;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#finish('cancel');
    this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#finish('cancel');
  }

  #initialize() {
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    try {
      this.offer = JSON.parse(this.refs.config.textContent || 'null');
    } catch {
      this.offer = null;
    }
    const { signal } = this.#listeners;
    this.refs.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.keepJars();
    }, { signal });
    this.refs.dialog.addEventListener('close', () => {
      if (!this.refs.dialog.open) this.#finish('jars');
    }, { signal });
    this.refs.dialog.addEventListener('click', (event) => {
      if (event.target !== this.refs.dialog) return;
      const bounds = this.refs.dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right ||
          event.clientY < bounds.top || event.clientY > bounds.bottom) this.keepJars();
    }, { signal });
  }

  /**
   * Resolves the customer's choice before the existing PDP cart action runs.
   * Incompatible or non-saving offers preserve the original order.
   * Inventory is validated by the existing cart action after the customer chooses.
   * @param {{originalPrice: number, subscription: boolean}} selection
   * @returns {Promise<'pouch' | 'jars' | 'cancel'>}
   */
  choose({ originalPrice, subscription }) {
    const offer = this.offer;
    const price = subscription ? offer?.subscriptionPrice : offer?.price;
    if (!offer?.id || !price || !Number.isFinite(originalPrice) || price >= originalPrice ||
        (subscription && !offer.sellingPlanId) || (!subscription && offer.requiresSellingPlan) ||
        typeof this.refs.dialog.showModal !== 'function') return Promise.resolve('jars');
    if (this.#resolve) return Promise.resolve('cancel');

    const formatter = new Intl.NumberFormat(this.dataset.locale || 'en', {
      style: 'currency', currency: this.dataset.currency || 'USD',
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    });
    this.refs.originalPrice.textContent = formatter.format(originalPrice / 100);
    this.refs.pouchPrice.textContent = formatter.format(price / 100);
    const percent = Math.round((originalPrice - price) / originalPrice * 100);
    this.refs.saving.textContent = (this.dataset.saveLabel || '').replace('[percent]', String(percent));

    return new Promise((resolve) => {
      this.#resolve = resolve;
      this.#ownsScrollLock = !document.documentElement.hasAttribute('scroll-lock');
      document.documentElement.setAttribute('scroll-lock', '');
      try {
        this.refs.dialog.showModal();
      } catch {
        this.#finish('cancel');
      }
    });
  }

  /** Accepts the replacement. @param {MouseEvent} [event] */
  acceptPouch(event) {
    this.#finish('pouch');
  }

  /** Keeps the original three jars, including Close and Escape. @param {MouseEvent} [event] */
  keepJars(event) {
    this.#finish('jars');
  }

  /** @param {'pouch' | 'jars' | 'cancel'} choice */
  #finish(choice) {
    const resolve = this.#resolve;
    this.#resolve = undefined;
    if (this.refs.dialog.open) this.refs.dialog.close();
    if (this.#ownsScrollLock) document.documentElement.removeAttribute('scroll-lock');
    this.#ownsScrollLock = false;
    resolve?.(choice);
  }
}

if (!customElements.get('powder-pouch-upsell')) customElements.define('powder-pouch-upsell', PowderPouchUpsell);
